import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, TrendingUp, AlertTriangle, Loader2, ShieldAlert, Sparkles, RefreshCw, Check } from "lucide-react";
import { ScoreBar, ValueBox, tamColor, scoreColor, fmtTamScore } from "./ScoreBar";
import ProbabilityCircle from "./ProbabilityCircle";
import CompanyQualCard from "./CompanyQualCard";
import ThesisDownloadButtons from "./ThesisDownloadButtons";
import HoverTip from "@/components/HoverTip";
import { QUAL_TIPS } from "@/lib/qualTips";

const DIMS = ["competitive_position", "sector_momentum", "management_quality", "financial_resilience", "trend_exposure"];

// TAM is expressed in USD billions (miles de millones). Show $B, or $T when ≥1000.
function fmtTam(busd) {
    if (busd == null || isNaN(busd)) return null;
    if (busd >= 1000) return `$${(busd / 1000).toFixed(busd >= 10000 ? 0 : 1)} T`;
    if (busd >= 10) return `$${Math.round(busd)} B`;
    return `$${busd} B`;
}

function TamBadge({ busd, label, note, tip, big = false }) {
    const txt = fmtTam(busd);
    if (!txt) return null;
    const inner = (
        <div className="flex flex-col items-center gap-0.5" data-testid="tam-badge">
            <div className={`font-mono font-bold leading-none text-[#1E7D45] ${big ? "text-2xl" : "text-base"}`}>{txt}</div>
            <span className="overline text-[#4A4A4A] text-center leading-tight">{label}</span>
        </div>
    );
    const text = [tip, note].filter(Boolean).join("\n\n") || "TAM en miles de millones de USD";
    return <HoverTip text={text} maxWidth={320}><div className="cursor-help">{inner}</div></HoverTip>;
}

function ContraSection({ contra, isTrend, canGenerate, onGenerate, generating }) {
    // Not generated yet → on-demand button (saves credits).
    if (!contra) {
        if (!canGenerate) return null;
        return (
            <div className="border border-[#B32A22]/40 bg-[#FBEAE8] px-4 py-3 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="contra-cta-wrap">
                <div className="flex items-center gap-2 text-sm text-[#7a1d17]">
                    <ShieldAlert size={16} className="shrink-0" />
                    <span>{isTrend
                        ? <>Toda tendencia crea ganadores y <strong>perdedores</strong>. Genera la contratesis: sectores y empresas perjudicados <em>porque</em> esta tendencia avanza.</>
                        : <>Genera la <strong>contratesis</strong>: qué cambios estructurales, al ocurrir, dejarían a esta empresa en el lado perdedor.</>}</span>
                </div>
                <button
                    onClick={onGenerate}
                    disabled={generating}
                    className="text-xs uppercase tracking-[0.12em] font-semibold border border-[#B32A22] text-[#B32A22] px-3 py-1.5 hover:bg-[#B32A22] hover:text-white transition-colors flex items-center gap-2 shrink-0 disabled:opacity-60"
                    data-testid="contra-generate-btn"
                >
                    {generating ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
                    {generating ? "Generando…" : "Añadir contratesis"}
                </button>
            </div>
        );
    }
    // Generated → red, low-visual-presence warning block.
    return (
        <div className="border border-[#B32A22]/50 bg-[#FBEAE8] px-4 py-3 mb-6" data-testid="contra-section">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="overline text-[#B32A22] flex items-center gap-1"><ShieldAlert size={12} /> {isTrend ? "Contratesis · perdedores de la tendencia" : "Contratesis · riesgo de disrupción"}</div>
                    {contra.summary && <p className="text-sm text-[#7a1d17] mt-1 leading-relaxed max-w-3xl">{contra.summary}</p>}
                </div>
                <ProbabilityCircle value={contra.probability} rationale={contra.probability_rationale} label="Prob. contratesis" size="sm" testid="contra-probability" />
            </div>

            {isTrend ? (
                <>
                    {contra.value_chain?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {contra.value_chain.map((v, i) => (
                                <div key={i} className="border border-[#B32A22]/30 bg-white/60 px-2 py-1 max-w-xs">
                                    <div className="text-xs font-semibold text-[#7a1d17]">{v.stage}</div>
                                    <div className="text-[11px] text-[#7a1d17]/80 leading-snug">{v.description}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {contra.companies?.length > 0 && (
                        <div className="mt-3">
                            <div className="overline text-[#B32A22] mb-1">Empresas más perjudicadas</div>
                            <div className="space-y-1">
                                {contra.companies.map((c, i) => (
                                    <div key={i} className="text-xs text-[#7a1d17] flex gap-2" data-testid={`contra-company-${c.ticker || i}`}>
                                        {c.ticker
                                            ? <Link to={`/company/${c.ticker}`} className="font-mono font-semibold underline shrink-0">{c.ticker}</Link>
                                            : <span className="font-semibold shrink-0">{c.name}</span>}
                                        <span>{c.harm_reason}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                contra.losing_trends?.length > 0 && (
                    <div className="mt-3 space-y-1">
                        <div className="overline text-[#B32A22] mb-1">Tendencias perjudiciales</div>
                        {contra.losing_trends.map((t, i) => (
                            <div key={i} className="text-xs text-[#7a1d17]">
                                <span className="font-semibold">{t.name}:</span> {t.harm_reason}
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
}

function SourcesList({ sources }) {
    if (!sources || !sources.length) return null;
    return (
        <div className="mt-8" data-testid="thesis-sources">
            <div className="overline text-[#4A4A4A] mb-2">Fuentes (búsqueda web en vivo)</div>
            <ul className="space-y-1">
                {sources.slice(0, 12).map((s, i) => (
                    <li key={i} className="text-xs flex gap-2">
                        <span className="font-mono text-[#4A4A4A]">[{i + 1}]</span>
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                           className="text-[#052049] hover:underline flex items-center gap-1 truncate">
                            {s.title || s.url}
                            <ExternalLink size={11} className="shrink-0" />
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TamScoreBadge({ data, loading, ticker }) {
    const isLoading = loading && !data;
    const v = data ? data.tam_score : null;
    const txt = isLoading ? null : (v == null || isNaN(v) ? null : v.toFixed(2));
    // Nothing to show (computed but null) and not loading → render nothing.
    if (!isLoading && txt == null) return null;
    const color = isLoading ? "#9CA3AF" : tamColor(v);
    const note = QUAL_TIPS.tamThesis +
        (data && data.stage_tam_busd != null && data.projected_revenue_busd != null ? `\n\nEn esta empresa: TAM del eslabón ${fmtTam(data.stage_tam_busd)} · ingresos a +2 años ${fmtTam(data.projected_revenue_busd)}.` : "");
    const row = (
        <div className="flex items-center justify-end gap-2" data-testid={isLoading ? `tam-score-loading-${ticker}` : `tam-score-${ticker}`}>
            <span className="overline text-[#4A4A4A] leading-tight text-right">TAM Score</span>
            {isLoading
                ? <div className="w-11 h-10 border-2 flex items-center justify-center shrink-0" style={{ borderColor: "#6B7280" }}><Loader2 size={14} className="animate-spin text-[#6B7280]" /></div>
                : <ValueBox text={txt} color={color} />}
        </div>
    );
    return isLoading ? row : <HoverTip text={note} maxWidth={320}><div className="cursor-help">{row}</div></HoverTip>;
}

/** Score with a two-line label on the LEFT and the boxed number on the RIGHT,
 *  so the boxes align flush to the card's right edge. */
function ScoreStatRight({ value, label, testid, tip }) {
    const row = (
        <div className="flex items-center justify-end gap-2" data-testid={testid}>
            <span className="overline text-[#4A4A4A] leading-tight text-right">{label}</span>
            <ValueBox text={value == null ? "—" : value} color={scoreColor(value)} />
        </div>
    );
    return tip ? <HoverTip text={tip} maxWidth={320}><div className="cursor-help">{row}</div></HoverTip> : row;
}

// Competitive role badge. 'disruptor' is reserved for genuine paradigm-shift / risky
// bets that threaten the leader / radically-superior approaches; every other non-leader
// is a 'competitor'.
function catBadge(category) {
    if (category === "disruptor") return { txt: "Disruptor", cls: "bg-[#B32A22] text-white" };
    if (category === "leader") return { txt: "Líder", cls: "bg-[#052049] text-[#FDF1E6]" };
    return { txt: "Competidor", cls: "bg-[#4A4A4A] text-white" };
}

function CompanyCard({ c, stageTam }) {
    const badge = catBadge(c.category);
    const tamData = { tam_score: c.tam_score, projected_revenue_busd: c.projected_revenue_busd, stage_tam_busd: stageTam };
    return (
        <div className="border border-black bg-white p-5" data-testid={`thesis-company-${c.ticker}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <span
                        className={`inline-block text-[10px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 mb-1 ${badge.cls}`}
                        data-testid={`category-${c.ticker}`}
                    >
                        {badge.txt}
                    </span>
                    <div className="font-serif text-xl font-medium leading-tight">{c.name}</div>
                    <div className="overline text-[#4A4A4A] mt-1">{c.value_chain_role}</div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <ScoreStatRight value={c.overall_score} label={<>Score<br />tesis</>} testid={`overall-${c.ticker}`} tip={QUAL_TIPS.scoreThesis} />
                    <TamScoreBadge data={tamData} loading={false} ticker={c.ticker} />
                </div>
            </div>

            {c.ticker && (
                <Link to={`/company/${c.ticker}`}
                      className="inline-flex items-center gap-1 mt-3 font-mono text-sm font-semibold bg-black text-[#FDF1E6] px-2 py-1 hover:bg-[#052049] transition-colors"
                      data-testid={`company-link-${c.ticker}`}>
                    {c.ticker} <ArrowRight size={13} />
                </Link>
            )}

            {c.why && <p className="text-sm mt-3 text-[#1a1a1a] leading-relaxed">{c.why}</p>}

            <div className="grid grid-cols-2 gap-x-5 gap-y-2 mt-4">
                {DIMS.map((d) => <ScoreBar key={d} dimension={d} value={(c.scores || {})[d]} />)}
            </div>

            {c.thesis && (
                <div className="mt-4 border-l-2 border-[#052049] pl-3">
                    <div className="overline text-[#4A4A4A] mb-1">Tesis</div>
                    <p className="text-sm leading-relaxed">{c.thesis}</p>
                </div>
            )}
            {c.key_risks && (
                <div className="mt-3 flex gap-2 text-xs text-[#B32A22]">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>{c.key_risks}</span>
                </div>
            )}
        </div>
    );
}

const _norm = (s) => (s || "").trim().toLowerCase();

function StageRow({ group }) {
    const { stage, leaders, disruptors } = group;
    return (
        <div className="mb-8" data-testid={`stage-row-${_norm(stage.stage).slice(0, 16)}`}>
            <div className="flex items-end justify-between gap-3 border-b-2 border-black pb-1.5 mb-3">
                <div className="min-w-0">
                    <div className="font-serif text-lg font-medium leading-tight">{stage.stage}</div>
                    {stage.description && <div className="text-xs text-[#4A4A4A] leading-snug mt-0.5">{stage.description}</div>}
                </div>
                <TamBadge busd={stage.tam_busd} label="TAM 2027e" tip={STAGE_TAM_TIP} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <div className="overline text-[#052049] mb-2">Líderes establecidos</div>
                    <div className="space-y-4">
                        {leaders.length
                            ? leaders.map((c, i) => <CompanyCard key={i} c={c} stageTam={stage.tam_busd} />)
                            : <div className="text-xs text-[#9CA3AF] border border-dashed border-black/20 p-3">Sin líder identificado en este eslabón.</div>}
                    </div>
                </div>
                <div>
                    <div className="overline text-[#B32A22] mb-2">Competidores / disruptores</div>
                    <div className="space-y-4">
                        {disruptors.length
                            ? disruptors.map((c, i) => <CompanyCard key={i} c={c} stageTam={stage.tam_busd} />)
                            : <div className="text-xs text-[#9CA3AF] border border-dashed border-black/20 p-3">Sin competidores identificados en este eslabón.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CompaniesByStage({ valueChain, companies }) {
    const stages = valueChain || [];
    const used = new Set();
    const groups = stages.map((s) => {
        const inStage = (companies || []).filter((c) => _norm(c.value_chain_role) === _norm(s.stage));
        inStage.forEach((c) => used.add(c.ticker));
        return {
            stage: s,
            leaders: inStage.filter((c) => c.category === "leader"),
            disruptors: inStage.filter((c) => c.category !== "leader"),
        };
    });
    const leftover = (companies || []).filter((c) => !used.has(c.ticker));
    if (leftover.length) {
        groups.push({
            stage: { stage: stages.length ? "Otros" : "Empresas", description: "", tam_busd: null },
            leaders: leftover.filter((c) => c.category === "leader"),
            disruptors: leftover.filter((c) => c.category !== "leader"),
        });
    }
    return <>{groups.map((g, i) => <StageRow key={i} group={g} />)}</>;
}

const HEADER_TAM_TIP =
    "TAM estimado de la tesis: la SUMA de los TAM de sus eslabones (abajo). Son los ingresos anuales que se prevé que obtengan en 2027 las empresas de esta cadena de valor por la tendencia, en miles de millones de USD; no el volumen de pagos o de activos que pasan por ella.";
const STAGE_TAM_TIP =
    "TAM del eslabón: ingresos anuales previstos en 2027 para las empresas de este eslabón por esta tendencia (miles de millones de USD). Los eslabones se reparten el mercado sin solaparse y su suma es el TAM de la tesis. Es el TAM que usa el TAM Score de las empresas de este eslabón.";

const DRIVER_TAM_TIP =
    "TAM estimado de la tesis de este motor: la suma de los TAM de los eslabones de su cadena de valor (ingresos anuales que se prevé que obtengan en 2027 las empresas de esa cadena por esta tendencia, en USD). Es el mismo que verás en el encabezado al entrar en la tesis.";
const ROLE_TAM_TIP =
    "TAM del eslabón en el que participa la empresa dentro de la tesis de este motor: los ingresos anuales previstos en 2027 para ese eslabón (en USD). Es el que usa su TAM Score: Score tesis / 100 × TAM del eslabón ÷ ingresos de la empresa a +2 años.";

/** A growth driver of the company's plan. Since 14-oct-2026 every driver is developed as
 *  ONE trend thesis right after the plan (no partitions or merges): the card shows that
 *  thesis's header TAM next to the title and the company's link of its value chain with
 *  that link's TAM — the same figures found inside the thesis. */
function NewThesisCard({ t, dt, partsDev = [], generating = false }) {
    const slug = _norm(t.name).slice(0, 12);
    const tam = fmtTam(dt?.tam_busd);
    // Only the developed thesis's link (the plan's own role is a loose label, e.g. «Líder»).
    const role = dt?.in_chain ? dt.role : null;
    const roleTam = dt?.in_chain ? fmtTam(dt.role_tam_busd) : null;
    return (
        <div className="border border-black bg-white p-5 flex flex-col" data-testid={`thesis-trend-${slug}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="font-serif text-xl sm:text-2xl font-medium leading-tight flex items-start gap-2 min-w-0">
                    <TrendingUp size={20} className="text-[#052049] shrink-0 mt-1" />
                    <span className="min-w-0 break-words">{t.name}</span>
                </div>
                {tam && (
                    <HoverTip text={DRIVER_TAM_TIP} maxWidth={320}>
                        <div className="cursor-help flex flex-col items-end shrink-0 pt-1" data-testid={`new-thesis-tam-${slug}`}>
                            <span className="font-mono font-bold text-lg leading-none text-[#1E7D45]">{tam}</span>
                            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#4A4A4A] mt-1 whitespace-nowrap">TAM estimado</span>
                        </div>
                    </HoverTip>
                )}
            </div>
            {(t.type === "actual" || t.type === "futura") && (
                <span
                    className="inline-block w-fit mt-2 text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5"
                    style={t.type === "actual"
                        ? { background: "#052049", color: "#FDF1E6" }
                        : { background: "#FBF3E0", color: "#7a5a10", border: "1px solid #B8860B" }}
                    data-testid={`thesis-type-${slug}`}
                    title={t.type === "actual" ? "Driver de crecimiento actual (negocio en expansión)" : "Apuesta de crecimiento futuro / adyacencia"}
                >
                    {t.type === "actual" ? "Crecimiento actual" : "Apuesta futura"}
                </span>
            )}

            {dt ? (
                <div className="mt-3 inline-flex items-center gap-2 w-fit border border-[#1E7D45]/50 bg-[#F0F7F2] px-3 py-1.5 text-sm" data-testid={`core-whole-note-${slug}`}>
                    <Check size={15} className="text-[#1E7D45]" />
                    <span className="font-semibold text-[#1E7D45]">Tesis generada</span>
                    <Link to={`/thesis/${dt.thesis_id}`} className="underline font-medium text-[#1a1a1a]" data-testid={`core-whole-link-${slug}`}>Ver la tesis</Link>
                </div>
            ) : partsDev.length > 0 ? (
                /* Plans developed by parts before 14-oct-2026. */
                <div className="mt-3 border border-[#1E7D45]/50 bg-[#F0F7F2] px-3 py-1.5 text-sm" data-testid={`core-split-note-${slug}`}>
                    <div className="font-semibold text-[#1E7D45] flex items-center gap-1.5"><Check size={15} /> Tesis generada por partes</div>
                    <ul className="mt-1 space-y-0.5">
                        {partsDev.map((p, i) => (
                            <li key={i}><Link to={`/thesis/${p.developed_id}`} className="underline text-[13px]">{p.split}</Link></li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="mt-3 text-[12px] text-[#4A4A4A] inline-flex items-center gap-1.5" data-testid={`pending-gen-${slug}`}>
                    {generating ? <><Loader2 size={13} className="animate-spin" /> Generando su tesis…</> : <>Tesis pendiente de generar</>}
                </div>
            )}

            {t.headline ? (
                <p className="text-[15px] mt-3 leading-snug font-semibold text-[#1a1a1a]" data-testid={`thesis-headline-${slug}`}>{t.headline}</p>
            ) : (
                t.fit_description && <p className="text-sm mt-3 leading-relaxed">{t.fit_description}</p>
            )}
            {role && (
                <div className="mt-3 text-sm sm:text-base text-[#1a1a1a] leading-snug" data-testid={`thesis-role-${slug}`}>
                    <span className="font-semibold">Rol en la cadena:</span> {role}
                    {roleTam && (
                        <div className="mt-1">
                            <HoverTip text={ROLE_TAM_TIP} maxWidth={320}>
                                <span className="cursor-help inline-flex items-baseline gap-1.5" data-testid={`thesis-role-tam-${slug}`}>
                                    <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4A4A4A]">TAM del eslabón</span>
                                    <span className="font-mono font-bold text-[#1E7D45]">{roleTam}</span>
                                </span>
                            </HoverTip>
                        </div>
                    )}
                </div>
            )}
            {dt && !dt.in_chain && (
                <div className="mt-2 text-xs text-[#7a5a10]" data-testid={`thesis-not-in-chain-${slug}`}>
                    La empresa no figura en la cadena de valor de esta tesis, así que no suma TAM Score.
                </div>
            )}
            {(t.headline ? t.fit_description : t.rationale) && (
                <div className="mt-3 border-l-2 pl-3 border-[#9CA3AF]">
                    <p className="text-sm leading-relaxed text-[#1a1a1a]">{t.headline ? t.fit_description : t.rationale}</p>
                </div>
            )}
        </div>
    );
}

export default function ThesisResult({ thesis, canGenerateContra = false, onGenerateContra, generatingContra = false, onGeneratePlan, generatingPlan = false }) {
    const isTrend = thesis?.type === "trend";
    const ticker = thesis?.company?.ticker;
    const inflight = thesis?.inflight_count || 0;

    // Theses developed by parts (plans executed before 14-oct-2026), per driver.
    const partsDev = useMemo(() => {
        const byCore = {};
        (thesis?.split_dev || []).forEach((e) => {
            if (e.whole || !e.split) return;
            (byCore[_norm(e.core)] || (byCore[_norm(e.core)] = [])).push({ split: e.split, developed_id: e.developed_id });
        });
        return byCore;
    }, [thesis?.split_dev]);

    // Drivers merged away in old plans (before 14-oct-2026) stay hidden.
    const drivers = useMemo(() => (thesis?.trends || []).filter((t) => !t.merged_into), [thesis?.trends]);
    const devTheses = thesis?.driver_theses || {};
    // Drivers whose thesis is still missing (failed or interrupted generation, or a plan
    // saved before every driver was generated automatically).
    const pendingDrivers = drivers.filter((t) => !devTheses[_norm(t.name)] && !(partsDev[_norm(t.name)] || []).length).map((t) => t.name);
    const pendingCount = pendingDrivers.length;

    if (!thesis) return null;

    return (
        <div data-testid="thesis-result">
            {/* Header */}
            <div className="border border-black bg-white p-6 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="overline text-[#B32A22] mb-1">
                            {isTrend ? "Tesis → Cadena de valor" : "Empresa → Tesis"}
                        </div>
                        <h1 className="font-serif text-3xl sm:text-4xl font-medium leading-tight">{thesis.title}</h1>
                        {!isTrend && thesis.company?.ticker && (
                            <Link to={`/company/${thesis.company.ticker}`}
                                  className="inline-flex items-center gap-1 mt-2 font-mono text-sm font-semibold bg-black text-[#FDF1E6] px-2 py-1 hover:bg-[#052049] transition-colors"
                                  data-testid={`company-link-${thesis.company.ticker}`}>
                                {thesis.company.ticker} <ArrowRight size={13} />
                            </Link>
                        )}
                    </div>
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:gap-5 shrink-0 w-full sm:w-auto">
                        {isTrend && thesis.tam?.global_busd != null && (
                            <TamBadge busd={thesis.tam.global_busd} label={`TAM ${thesis.tam?.year || 2027}e`} tip={HEADER_TAM_TIP} big />
                        )}
                        <ProbabilityCircle value={thesis.probability} rationale={thesis.probability_rationale} label="Prob. de la tesis" testid="thesis-probability" />
                    </div>
                </div>
                {thesis.summary && <p className="text-base mt-4 leading-relaxed text-[#1a1a1a]">{thesis.summary}</p>}
                {thesis.id && <ThesisDownloadButtons thesisId={thesis.id} title={thesis.title} className="mt-4 flex-wrap" />}
            </div>

            {!isTrend && thesis.no_changes && (
                <div className="border border-[#052049]/40 bg-[#EAF0F7] px-4 py-2.5 mb-6 flex items-start gap-2 text-xs text-[#052049]" data-testid="no-changes-note">
                    <ShieldAlert size={14} className="shrink-0 mt-0.5" />
                    <span>Sin novedades relevantes: hemos vuelto a analizar la empresa y no hay cambios significativos respecto a tu tesis guardada, así que la <strong>conservamos</strong> tal cual.</span>
                </div>
            )}

            {!isTrend && thesis.changes?.length > 0 && (
                <div className="border border-[#1E7D45]/40 bg-[#E8F3EC] px-4 py-3 mb-6 text-xs text-[#13532f]" data-testid="changes-note">
                    <div className="font-semibold flex items-center gap-2 mb-1.5">
                        <RefreshCw size={13} /> Tesis actualizada · {thesis.changes.length} cambio{thesis.changes.length > 1 ? "s" : ""} detectado{thesis.changes.length > 1 ? "s" : ""}
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5">
                        {thesis.changes.map((c, i) => (<li key={i}>{c}</li>))}
                    </ul>
                </div>
            )}

            {isTrend && thesis.omitted_companies?.length > 0 && (
                <div className="border border-[#B8860B]/50 bg-[#FBF3E0] px-4 py-2.5 mb-6 flex items-start gap-2 text-xs text-[#7a5a10]" data-testid="omitted-companies-note">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5 text-[#B8860B]" />
                    <span>
                        <strong>{thesis.omitted_companies.map((o) => o.name || o.ticker).join(", ")}</strong>{" "}
                        {thesis.omitted_companies.length > 1 ? "no aparecen" : "no aparece"} en esta tesis porque ya {thesis.omitted_companies.length > 1 ? "están" : "está"} en{" "}
                        {thesis.omitted_for_thesis?.id
                            ? <Link to={`/thesis/${thesis.omitted_for_thesis.id}`} className="font-bold underline" data-testid="omitted-thesis-link">{thesis.omitted_for_thesis.title}</Link>
                            : "otra tesis que encaja"}.{" "}
                        Se {thesis.omitted_companies.length > 1 ? "han" : "ha"} omitido para no duplicar su valor.
                    </span>
                </div>
            )}

            {/* Contra-thesis (between header and value chain) */}
            <ContraSection
                contra={thesis.contra}
                isTrend={isTrend}
                canGenerate={canGenerateContra}
                onGenerate={onGenerateContra}
                generating={generatingContra}
            />

            {/* Value chain (trend only) */}
            {/* Value chain + companies by stage (leaders / disruptors), trend only */}
            {isTrend ? (
                <>
                    <div className="flex items-baseline justify-between gap-2 mb-3">
                        <div className="overline text-[#4A4A4A]">Cadena de valor · líderes vs. competidores/disruptores</div>
                        <div className="overline text-[#9CA3AF] hidden sm:block">TAM 2027e por eslabón · su suma es el TAM de la tesis</div>
                    </div>
                    <CompaniesByStage valueChain={thesis.value_chain} companies={thesis.companies} />
                </>
            ) : (
                <>
                    {/* 1.1a — CompanyQualCard (restored Jun 2026, filtered by source_company_thesis_id):
                        shows ONLY trend theses that were BORN from THIS company's plan,
                        not auto-included memberships from other plans. From the start of a
                        generation it lists every driver, and each row fills in as its thesis
                        lands (2-nov-2026); hidden only when there is nothing to show. */}
                    {thesis.company?.ticker && (
                        <CompanyQualCard
                            ticker={thesis.company.ticker}
                            hideEmpty
                            fromCompanyId={thesis.id}
                            refreshKey={`${thesis.updated_at || thesis.id}|${Object.keys(devTheses).length}|${inflight}`}
                            pendingDrivers={pendingDrivers}
                            generating={inflight > 0}
                        />
                    )}

                    <div className="flex items-center gap-1.5 mb-2">
                        <div className="overline text-[#4A4A4A]">Motores de crecimiento</div>
                        <HoverTip text={"Cada motor de crecimiento de la empresa se desarrolla en su propia tesis de tendencia, en la misma generación. Junto al título, el TAM estimado de esa tesis (la suma de sus eslabones); en «Rol en la cadena», el eslabón en el que participa la empresa y su TAM, que es el que usa su TAM Score."} maxWidth={340}>
                            <span className="cursor-help text-[#9CA3AF] text-xs">ⓘ</span>
                        </HoverTip>
                    </div>
                    {inflight > 0 ? (
                        <div className="mb-3 border border-black/15 bg-[#F7F2EA] px-3 py-2 text-[12px] text-[#4A4A4A] leading-snug flex items-center gap-2" data-testid="plan-inflight-notice">
                            <Loader2 size={14} className="animate-spin shrink-0" />
                            <span>Generando las tesis de los motores: {inflight === 1 ? "queda 1" : `quedan ${inflight}`}. Esta página se actualiza sola.</span>
                        </div>
                    ) : pendingCount > 0 && thesis.id && onGeneratePlan ? (
                        <div className="mb-3 border border-[#B32A22]/40 bg-[#FBEAE8] px-3 py-2 text-[12px] text-[#7a1d17] leading-snug flex items-center gap-2 flex-wrap" data-testid="plan-pending-notice">
                            <span className="flex-1 min-w-[12rem]">{pendingCount === 1 ? "Falta generar la tesis de 1 motor." : `Falta generar la tesis de ${pendingCount} motores.`}</span>
                            <button
                                onClick={() => onGeneratePlan(thesis.id)}
                                disabled={generatingPlan}
                                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-semibold bg-black text-[#FDF1E6] px-3 py-1.5 hover:bg-[#052049] transition-colors disabled:opacity-50"
                                data-testid="resume-plan-btn"
                            >
                                {generatingPlan ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generar las que faltan
                            </button>
                        </div>
                    ) : null}
                    {drivers.length ? (
                        <div className="grid md:grid-cols-2 gap-4" data-testid="new-trends-list">
                            {drivers.map((t, i) => (
                                <NewThesisCard
                                    key={i} t={t}
                                    dt={devTheses[_norm(t.name)]}
                                    partsDev={partsDev[_norm(t.name)] || []}
                                    generating={inflight > 0}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-[#4A4A4A] border border-dashed border-black/20 p-4" data-testid="new-trends-empty">
                            No se han identificado motores de crecimiento para {ticker || "la empresa"}.
                        </div>
                    )}
                </>
            )}

            <SourcesList sources={thesis.sources} />
        </div>
    );
}
